import React, { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Paperclip, FileText, Camera } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Button } from "@/components/ui-kit";
import DocViewer from "@/components/DocViewer";
import {
  getMeta, listIngredients, createIngredient, updateIngredient, deleteIngredient, uploadFile,
} from "@/lib/api";
import { resizeImage } from "@/lib/image";

export default function IngredientCatalog() {
  const { data: meta } = useSWR("meta", getMeta);
  const { data: ingredients, mutate } = useSWR("ingredients", listIngredients);
  const allergens = meta?.allergens || [];
  const [editing, setEditing] = useState(null); // { id, name, allergens: [], datasheets: [] }
  const [saving, setSaving] = useState(false);
  const [uploadingFt, setUploadingFt] = useState(false);
  const [viewer, setViewer] = useState(null);

  const nameOf = (aid) => allergens.find((a) => a.id === aid)?.name || aid;
  const startNew = () => setEditing({ id: null, name: "", allergens: [], datasheets: [], ft_details: "", track_reception: false, match_keywords: "" });
  const startEdit = (ing) => setEditing({ id: ing.id, name: ing.name, allergens: [...ing.allergens], datasheets: [...(ing.datasheets || [])], ft_details: ing.ft_details || "", track_reception: !!ing.track_reception, match_keywords: ing.match_keywords || "" });
  const toggleAllergen = (aid) =>
    setEditing((e) => ({
      ...e,
      allergens: e.allergens.includes(aid) ? e.allergens.filter((x) => x !== aid) : [...e.allergens, aid],
    }));

  const handleFt = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingFt(true);
    try {
      for (const file of files) {
        const f = await resizeImage(file); // PDFs passen sense canvis
        const { url } = await uploadFile(f);
        setEditing((s) => ({ ...s, datasheets: [...s.datasheets, url] }));
      }
    } catch {
      toast.error("No s'ha pogut pujar el document");
    } finally {
      setUploadingFt(false);
    }
  };
  const removeDoc = (url) => setEditing((s) => ({ ...s, datasheets: s.datasheets.filter((u) => u !== url) }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Posa un nom");
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), allergens: editing.allergens, datasheets: editing.datasheets, ft_details: editing.ft_details, track_reception: editing.track_reception, match_keywords: editing.match_keywords.trim() };
      if (editing.id) await updateIngredient(editing.id, payload);
      else await createIngredient(payload);
      toast.success("Ingredient desat");
      setEditing(null);
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error desant");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ing) => {
    if (!window.confirm(`Eliminar "${ing.name}"?`)) return;
    try {
      await deleteIngredient(ing.id);
      toast.success("Eliminat");
      mutate();
    } catch {
      toast.error("Error eliminant");
    }
  };

  return (
    <>
      <Header title="Ingredients" />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Admin</div>
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Catàleg d'ingredients</h2>
          </div>
          {!editing && (
            <Button onClick={startNew} testid="ingredient-new"><Plus size={18} /> Nou</Button>
          )}
        </div>

        {editing && (
          <Card className="mt-6">
            <div className="space-y-4">
              <div>
                <div className="label-eyebrow mb-2">Nom de l'ingredient</div>
                <TextInput testid="ingredient-name" value={editing.name}
                  onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Ex: Farina de blat" />
              </div>
              <div>
                <div className="label-eyebrow mb-2">Al·lèrgens que conté</div>
                <div className="flex flex-wrap gap-2">
                  {allergens.map((a) => (
                    <button key={a.id} type="button" data-testid={`allergen-${a.id}`}
                      onClick={() => toggleAllergen(a.id)}
                      className={
                        "rounded-full border px-3 py-1.5 text-sm font-semibold transition-all " +
                        (editing.allergens.includes(a.id)
                          ? "border-terracotta bg-terracotta text-white"
                          : "border-line bg-white text-ink-soft hover:border-terracotta/40")
                      }>
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-forest/30 bg-forest-bg/40 p-3">
                <label className="flex cursor-pointer items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-ink">Matèria primera amb traçabilitat</div>
                    <div className="text-xs text-ink-soft">En elaborar, es demanarà de quin albarà surt (es suggereix el més recent).</div>
                  </div>
                  <input type="checkbox" checked={editing.track_reception}
                    onChange={(e) => setEditing((s) => ({ ...s, track_reception: e.target.checked }))}
                    className="h-5 w-5 shrink-0 accent-forest" />
                </label>
                {editing.track_reception && (
                  <div className="mt-3">
                    <div className="label-eyebrow mb-1.5">Paraules clau (separades per coma)</div>
                    <TextInput value={editing.match_keywords}
                      onChange={(e) => setEditing((s) => ({ ...s, match_keywords: e.target.value }))}
                      placeholder="Ex: pollastre,pollo" className="!h-12 text-sm" />
                  </div>
                )}
              </div>
              <div>
                <div className="label-eyebrow mb-2">Dades de la FT (text)</div>
                <TextArea testid="ingredient-ft-details" rows={5} value={editing.ft_details}
                  onChange={(e) => setEditing((s) => ({ ...s, ft_details: e.target.value }))}
                  placeholder="Codi intern, ingredients, dosi, al·lèrgens declarats, conservació…" />
              </div>
              <div>
                <div className="label-eyebrow mb-2">Documents FT (PDF o foto)</div>
                <div className="space-y-2">
                  {editing.datasheets.map((url) => (
                    <div key={url} className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
                      <FileText size={16} className="shrink-0 text-ink-soft" />
                      <button type="button" onClick={() => setViewer(url)} className="flex-1 truncate text-left text-sm text-terracotta underline">{url.split("/").pop()}</button>
                      <button type="button" onClick={() => removeDoc(url)} className="shrink-0 text-ink-soft hover:text-ember" aria-label="Treure"><X size={16} /></button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                      <Camera size={16} /> {uploadingFt ? "Pujant…" : "Càmera"}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFt} disabled={uploadingFt} />
                    </label>
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                      <Paperclip size={16} /> Document
                      <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleFt} disabled={uploadingFt} />
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={save} disabled={saving || uploadingFt} testid="ingredient-save"><Check size={18} /> {saving ? "Desant…" : "Desar"}</Button>
                <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /> Cancel·lar</Button>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-6 space-y-3">
          {ingredients?.length === 0 && !editing && (
            <Card className="text-center text-sm text-ink-soft">Encara no hi ha ingredients. Crea'n un amb "Nou".</Card>
          )}
          {ingredients?.map((ing) => (
            <Card key={ing.id} testid={`ingredient-${ing.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-ink">{ing.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {ing.allergens.length === 0 && <span className="text-xs text-ink-soft">Sense al·lèrgens</span>}
                    {ing.allergens.map((aid) => (
                      <span key={aid} className="rounded-full bg-ember-bg px-2 py-0.5 text-xs font-semibold text-ember">{nameOf(aid)}</span>
                    ))}
                  </div>
                  {ing.datasheets?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {ing.datasheets.map((url) => (
                        <button key={url} type="button" onClick={() => setViewer(url)}
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-ink-soft hover:text-terracotta">
                          <FileText size={12} /> Veure FT
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(ing)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={16} /></button>
                  <button onClick={() => remove(ing)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      {viewer && <DocViewer url={viewer} onClose={() => setViewer(null)} />}
    </>
  );
}
